import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useNavigate } from 'react-router-dom';
import { useCrossTabSync } from '@/hooks/useCrossTabSync';

interface UseSessionTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

// Chave do localStorage para compartilhar última atividade entre abas
const LAST_ACTIVITY_KEY = 'd12-session-last-activity';

export function useSessionTimeout({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onWarning,
  onTimeout,
}: UseSessionTimeoutOptions = {}) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Usar cross-tab sync para atividade compartilhada
  const { postMessage, hasOtherVisibleTabs } = useCrossTabSync();

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  /**
   * Obtém o timestamp da última atividade (compartilhado entre abas)
   */
  const getLastActivity = useCallback((): number => {
    try {
      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (stored) {
        return parseInt(stored, 10);
      }
    } catch {
      // Ignorar erros de localStorage
    }
    return Date.now();
  }, []);

  /**
   * Atualiza o timestamp de última atividade (compartilhado entre abas)
   */
  const setLastActivity = useCallback((timestamp: number) => {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, timestamp.toString());
    } catch {
      // Ignorar erros de localStorage
    }
  }, []);

  const handleTimeout = useCallback(async () => {
    // Verificar se há atividade recente em QUALQUER aba antes de fazer timeout
    const lastActivity = getLastActivity();
    const timeSinceActivity = Date.now() - lastActivity;
    const timeoutMs = timeoutMinutes * 60 * 1000;

    // Se houve atividade recente, não fazer timeout
    if (timeSinceActivity < timeoutMs) {
      console.log('[SessionTimeout] Activity detected in another tab, skipping timeout');
      return;
    }

    clearTimers();

    // Notificar outras abas sobre expiração de sessão
    postMessage('AUTH_SESSION_EXPIRED');

    // Call custom timeout handler if provided
    if (onTimeout) {
      onTimeout();
    }

    // Sign out and redirect
    await signOut();
    navigate('/login', {
      state: { message: 'Sua sessão expirou por inatividade.' }
    });
  }, [clearTimers, onTimeout, signOut, navigate, getLastActivity, timeoutMinutes, postMessage]);

  const handleWarning = useCallback(() => {
    // Verificar se realmente deve mostrar warning baseado na última atividade
    const lastActivity = getLastActivity();
    const timeSinceActivity = Date.now() - lastActivity;
    const warningThreshold = (timeoutMinutes - warningMinutes) * 60 * 1000;

    if (timeSinceActivity >= warningThreshold) {
      if (onWarning) {
        onWarning();
      }
    }
  }, [onWarning, getLastActivity, timeoutMinutes, warningMinutes]);

  /**
   * Reseta o timer de timeout (chamado quando há atividade)
   */
  const resetTimer = useCallback(() => {
    if (!user) return;

    // Atualizar timestamp de última atividade (compartilhado)
    const now = Date.now();
    setLastActivity(now);

    // Notificar outras abas sobre atividade
    postMessage('ACTIVITY_UPDATE', { timestamp: now });

    // Clear existing timers
    clearTimers();

    // Set warning timer (e.g., 5 minutes before timeout)
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    warningRef.current = setTimeout(handleWarning, warningTime);

    // Set timeout timer
    const timeoutTime = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(handleTimeout, timeoutTime);
  }, [user, timeoutMinutes, warningMinutes, clearTimers, handleWarning, handleTimeout, setLastActivity, postMessage]);

  /**
   * Verifica periodicamente se deve fazer timeout baseado na última atividade global
   */
  const checkForTimeout = useCallback(() => {
    if (!user) return;

    const lastActivity = getLastActivity();
    const timeSinceActivity = Date.now() - lastActivity;
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Se passou o tempo de timeout
    if (timeSinceActivity >= timeoutMs) {
      handleTimeout();
    }
    // Se passou o tempo de warning mas não de timeout
    else if (timeSinceActivity >= warningMs) {
      handleWarning();
    }
  }, [user, getLastActivity, timeoutMinutes, warningMinutes, handleTimeout, handleWarning]);

  // Track user activity
  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }

    // Inicializar última atividade se não existir
    const currentActivity = getLastActivity();
    if (!currentActivity || Date.now() - currentActivity > 60000) {
      setLastActivity(Date.now());
    }

    // Events to track for activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Throttle activity updates
    let lastUpdate = 0;
    const handleActivity = () => {
      const now = Date.now();
      // Throttle: só atualizar a cada 5 segundos
      if (now - lastUpdate > 5000) {
        lastUpdate = now;
        resetTimer();
      }
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Start the timer
    resetTimer();

    // Verificar periodicamente por timeout (a cada 30 segundos)
    // Isso garante que mesmo se o timer local for resetado,
    // verificamos a última atividade global
    checkIntervalRef.current = setInterval(checkForTimeout, 30000);

    // Cleanup
    return () => {
      clearTimers();
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, resetTimer, clearTimers, getLastActivity, setLastActivity, checkForTimeout]);

  // Handle visibility change (tab focus/blur)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        // Ao voltar para a aba, verificar se há outras abas ativas
        // Se houver, verificar a última atividade global
        const lastActivity = getLastActivity();
        const timeSinceActivity = Date.now() - lastActivity;
        const timeoutMs = timeoutMinutes * 60 * 1000;

        // Se passou muito tempo sem atividade em NENHUMA aba, fazer timeout
        if (timeSinceActivity >= timeoutMs) {
          handleTimeout();
        } else {
          // Resetar timer com base na última atividade
          resetTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetTimer, getLastActivity, timeoutMinutes, handleTimeout]);

  // Listen for storage changes (activity from other tabs)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== LAST_ACTIVITY_KEY) return;

      // Outra aba atualizou a atividade - resetar nossos timers
      if (event.newValue && user) {
        const newActivity = parseInt(event.newValue, 10);
        const now = Date.now();

        // Se a atividade é recente (últimos 10 segundos), considerar válida
        if (now - newActivity < 10000) {
          // Clear existing timers e reiniciar
          clearTimers();

          // Recalcular timers baseado na última atividade
          const timeSinceActivity = now - newActivity;
          const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000 - timeSinceActivity;
          const timeoutTime = timeoutMinutes * 60 * 1000 - timeSinceActivity;

          if (warningTime > 0) {
            warningRef.current = setTimeout(handleWarning, warningTime);
          }
          if (timeoutTime > 0) {
            timeoutRef.current = setTimeout(handleTimeout, timeoutTime);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user, clearTimers, timeoutMinutes, warningMinutes, handleWarning, handleTimeout]);

  return {
    resetTimer,
    getTimeUntilTimeout: () => {
      if (!user) return null;
      const lastActivity = getLastActivity();
      const elapsed = Date.now() - lastActivity;
      const remaining = (timeoutMinutes * 60 * 1000) - elapsed;
      return Math.max(0, remaining);
    },
    getTimeUntilWarning: () => {
      if (!user) return null;
      const lastActivity = getLastActivity();
      const elapsed = Date.now() - lastActivity;
      const remaining = ((timeoutMinutes - warningMinutes) * 60 * 1000) - elapsed;
      return Math.max(0, remaining);
    },
    hasOtherVisibleTabs,
  };
}
